/* Risk-aware UA/UC responsibility insight with level-based sorting. */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getValue } from "../utils";
import "./UaucOpenObservationUsersInsight.css";

const USERS_API = "https://jkumarapi.axiomos.in/user";

const RISK_LEVELS = {
  1: { label: "Low", tone: "low" },
  2: { label: "Moderate", tone: "moderate" },
  3: { label: "High", tone: "high" },
  4: { label: "Severe", tone: "severe" },
};

const getRiskLevel = (row) => {
  const source =
    row?.riskLevel?.value ??
    row?.riskLevel?.level ??
    row?.riskLevel ??
    row?.riskValue?.value ??
    row?.riskValue?.level ??
    row?.riskValue ??
    row?.riskRating ??
    row?.risk;

  const rawValue =
    source && typeof source === "object"
      ? (source.value ?? source.level ?? source.rating ?? source.name)
      : source;

  const numericLevel = Number(rawValue);
  if (
    Number.isInteger(numericLevel) &&
    numericLevel >= 1 &&
    numericLevel <= 4
  ) {
    return numericLevel;
  }

  const normalized = String(rawValue || "")
    .trim()
    .toLowerCase();

  const numericPrefix = normalized.match(/^([1-4])(?:\b|\s*[-:])/);
  if (numericPrefix) return Number(numericPrefix[1]);

  if (["low", "minor"].includes(normalized)) return 1;
  if (["moderate", "medium"].includes(normalized)) return 2;
  if (["high", "major"].includes(normalized)) return 3;
  if (["severe", "critical", "extreme"].includes(normalized)) return 4;

  return null;
};

const RiskBadge = ({ level }) => {
  const risk = RISK_LEVELS[level];

  if (!risk) {
    return (
      <span className="uauc-risk-badge uauc-risk-badge--unrated">
        Not rated
      </span>
    );
  }

  return (
    <span className={`uauc-risk-badge uauc-risk-badge--${risk.tone}`}>
      <span aria-hidden="true" />
      Level {level} · {risk.label}
    </span>
  );
};

const isClosed = (row) => String(row?.status || "").toLowerCase() === "closed";

const formatDateTime = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const extractApiArray = (payload) => {
  const candidates = [
    payload?.data?.data,
    payload?.data?.users,
    payload?.data?.result,
    payload?.data?.results,
    payload?.data,
    payload?.users,
    payload?.result,
    payload?.results,
    payload,
  ];

  for (const item of candidates) {
    if (Array.isArray(item)) return item;
  }

  return [];
};

const normalizeKey = (value) => {
  if (!value) return "";

  if (typeof value === "object") {
    return String(
      value._id ||
        value.id ||
        value.userId ||
        value.emailId ||
        value.name ||
        "",
    )
      .trim()
      .toLowerCase();
  }

  return String(value).trim().toLowerCase();
};

const getUserKey = (user) => {
  if (!user) return "";

  if (typeof user === "string") return normalizeKey(user);

  return normalizeKey(
    user._id ||
      user.id ||
      user.userId ||
      user.emailId ||
      user.email ||
      user.name,
  );
};

const getUserName = (user) => {
  if (!user) return "Unassigned";

  if (typeof user === "string") return user;

  return (
    user.name ||
    user.fullName ||
    user.userName ||
    user.employeeName ||
    user.emailId ||
    user.email ||
    user.userId ||
    "Unnamed User"
  );
};

const getUserEmail = (user) =>
  user?.emailId || user?.email || user?.officialEmail || "—";

const getUserPhone = (user) =>
  user?.phone || user?.phoneNumber || user?.mobile || user?.mobileNumber || "—";

const getUserDesignation = (user) =>
  user?.designation || user?.position || user?.department || "—";

const getUserRole = (user) => {
  const role = user?.role;

  if (!role) return user?.roleName || "—";
  if (typeof role === "string") return role;

  return role.roleName || role.name || role.title || role.label || "—";
};

const getAssignedUsers = (row) => {
  const assignedTo = row?.assignedTo;

  if (Array.isArray(assignedTo)) return assignedTo;
  if (assignedTo && typeof assignedTo === "object") return [assignedTo];
  if (assignedTo) return [assignedTo];

  const assignedName = getValue(row, "assignedTo.name");
  return assignedName ? [assignedName] : [];
};

const findUserFromMaster = (assignedUser, allUsers) => {
  const assignedKeys = [
    assignedUser?._id,
    assignedUser?.id,
    assignedUser?.userId,
    assignedUser?.emailId,
    assignedUser?.email,
    assignedUser?.name,
    typeof assignedUser === "string" ? assignedUser : "",
  ]
    .map(normalizeKey)
    .filter(Boolean);

  return (
    allUsers.find((user) => {
      const userKeys = [
        user?._id,
        user?.id,
        user?.userId,
        user?.emailId,
        user?.email,
        user?.name,
      ]
        .map(normalizeKey)
        .filter(Boolean);

      return userKeys.some((key) => assignedKeys.includes(key));
    }) || null
  );
};

const createUserGroup = (key, user) => ({
  key,
  user,
  openCount: 0,
  observations: [],
  latestDate: null,
  highestRisk: null,
  riskCounts: { 1: 0, 2: 0, 3: 0, 4: 0 },
});

const addObservationToGroup = (group, row) => {
  const riskLevel = getRiskLevel(row);
  const rowDate = new Date(row?.date || row?.createdAt || 0);

  group.openCount += 1;
  group.observations.push({ row, riskLevel });

  if (riskLevel) {
    group.highestRisk = Math.max(group.highestRisk || 0, riskLevel);
    group.riskCounts[riskLevel] += 1;
  }

  if (
    !Number.isNaN(rowDate.getTime()) &&
    (!group.latestDate || rowDate > group.latestDate)
  ) {
    group.latestDate = rowDate;
  }
};

const ObservationMiniTable = ({ observations = [], onOpenObservation }) => {
  if (!observations.length) {
    return (
      <div className="uauc-user-popup-empty">No open observations found.</div>
    );
  }

  return (
    <div className="uauc-user-popup-table-wrap">
      <table className="uauc-user-popup-table">
        <thead>
          <tr>
            <th>Report ID</th>
            <th>Project</th>
            <th>Area</th>
            <th>Risk</th>
            <th>Date</th>
            <th>Observation</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>
          {observations.map((item, index) => {
            const row = item.row;
            const riskLevel = item.riskLevel ?? getRiskLevel(row);

            return (
              <tr
                key={row?._id || row?.reportId || index}
                className={
                  riskLevel ? `uauc-observation-row--risk-${riskLevel}` : ""
                }
              >
                <td>{row?.reportId || "—"}</td>
                <td>{row?.project?.projectName || "—"}</td>
                <td>{row?.area || "—"}</td>
                <td>
                  <RiskBadge level={riskLevel} />
                </td>
                <td>{formatDateTime(row?.date || row?.createdAt)}</td>
                <td className="uauc-user-popup-observation">
                  {row?.observation || "—"}
                </td>
                <td>
                  <button
                    type="button"
                    className="uauc-user-popup-open-btn"
                    onClick={() => onOpenObservation?.(row)}
                  >
                    Open
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const UserDetailsPopup = ({ userGroup, onClose, onOpenObservation }) => {
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!userGroup) return null;

  const user = userGroup.user;

  return (
    <div
      className="uauc-user-popup"
      role="dialog"
      aria-modal="true"
      aria-label={`${getUserName(user)} open observations`}
      onMouseDown={onClose}
    >
      <div
        className="uauc-user-popup-box"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="uauc-user-popup-header">
          <div>
            <h3>{getUserName(user)}</h3>
            <p>
              {userGroup.openCount} open observation
              {userGroup.openCount === 1 ? "" : "s"} pending closure
            </p>
            <RiskBadge level={userGroup.highestRisk} />
          </div>

          <button
            type="button"
            className="uauc-user-popup-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="uauc-user-popup-body">
          <div className="uauc-user-detail-grid">
            <div>
              <span>User ID</span>
              <strong>{user?.userId || user?._id || "—"}</strong>
            </div>
            <div>
              <span>Name</span>
              <strong>{getUserName(user)}</strong>
            </div>
            <div>
              <span>Designation</span>
              <strong>{getUserDesignation(user)}</strong>
            </div>
            <div>
              <span>Role</span>
              <strong>{getUserRole(user)}</strong>
            </div>
            <div>
              <span>Email</span>
              <strong>{getUserEmail(user)}</strong>
            </div>
            <div>
              <span>Phone</span>
              <strong>{getUserPhone(user)}</strong>
            </div>
          </div>

          <div className="uauc-user-popup-section-title">
            Open Observations Assigned
          </div>

          <ObservationMiniTable
            observations={userGroup.observations}
            onOpenObservation={onOpenObservation}
          />
        </div>
      </div>
    </div>
  );
};

export default function UaucOpenObservationUsersInsight({
  rows = [],
  onOpenObservation,
}) {
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserGroup, setSelectedUserGroup] = useState(null);
  const [sortMode, setSortMode] = useState("risk-desc");

  useEffect(() => {
    let alive = true;

    const fetchUsers = async () => {
      try {
        const response = await axios.get(USERS_API);
        const users = extractApiArray(response);

        if (alive) setAllUsers(users);
      } catch (error) {
        console.error("Failed to fetch users for UA/UC insight", error);
      }
    };

    fetchUsers();

    return () => {
      alive = false;
    };
  }, []);

  const openRows = useMemo(() => {
    return (rows || []).filter((row) => !isClosed(row));
  }, [rows]);

  const userGroups = useMemo(() => {
    const map = new Map();

    openRows.forEach((row) => {
      const assignedUsers = getAssignedUsers(row);

      if (!assignedUsers.length) {
        const key = "unassigned";

        if (!map.has(key)) {
          map.set(key, createUserGroup(key, { name: "Unassigned" }));
        }

        const group = map.get(key);
        addObservationToGroup(group, row);

        return;
      }

      assignedUsers.forEach((assignedUser) => {
        const masterUser = findUserFromMaster(assignedUser, allUsers);
        const mergedUser =
          masterUser && typeof assignedUser === "object"
            ? { ...masterUser, ...assignedUser }
            : masterUser || assignedUser;

        const key = getUserKey(mergedUser) || getUserName(mergedUser);

        if (!map.has(key)) {
          map.set(key, createUserGroup(key, mergedUser));
        }

        const group = map.get(key);
        addObservationToGroup(group, row);
      });
    });

    return Array.from(map.values()).map((group) => ({
      ...group,
      observations: [...group.observations].sort((a, b) => {
        const riskDifference = (b.riskLevel || 0) - (a.riskLevel || 0);
        if (riskDifference) return riskDifference;

        return (
          new Date(b.row?.date || b.row?.createdAt || 0) -
          new Date(a.row?.date || a.row?.createdAt || 0)
        );
      }),
    }));
  }, [openRows, allUsers]);

  const sortedUserGroups = useMemo(() => {
    return [...userGroups].sort((a, b) => {
      if (sortMode === "risk-asc") {
        const aRisk = a.highestRisk || 5;
        const bRisk = b.highestRisk || 5;
        return aRisk - bRisk || b.openCount - a.openCount;
      }

      if (sortMode === "open-desc") {
        return (
          b.openCount - a.openCount ||
          (b.highestRisk || 0) - (a.highestRisk || 0)
        );
      }

      if (sortMode === "latest-desc") {
        return (
          (b.latestDate?.getTime?.() || 0) - (a.latestDate?.getTime?.() || 0) ||
          (b.highestRisk || 0) - (a.highestRisk || 0)
        );
      }

      return (
        (b.highestRisk || 0) - (a.highestRisk || 0) || b.openCount - a.openCount
      );
    });
  }, [userGroups, sortMode]);

  const majorUsers = useMemo(
    () => sortedUserGroups.slice(0, 5),
    [sortedUserGroups],
  );

  const totalOpen = openRows.length;
  const assignedUserCount = userGroups.filter(
    (group) => group.key !== "unassigned",
  ).length;
  const unassignedOpen =
    userGroups.find((group) => group.key === "unassigned")?.openCount || 0;
  const highestPending = Math.max(
    0,
    ...userGroups.map((group) => group.openCount),
  );

  const riskTotals = useMemo(() => {
    return openRows.reduce(
      (totals, row) => {
        const level = getRiskLevel(row);
        if (level) totals[level] += 1;
        else totals.unrated += 1;
        return totals;
      },
      { 1: 0, 2: 0, 3: 0, 4: 0, unrated: 0 },
    );
  }, [openRows]);

  if (!totalOpen) return null;

  return (
    <>
      <section className="uauc-users-insight">
        <div className="uauc-users-insight__header">
          <div>
            <h3>Open Observation Responsibility</h3>
            <p>
              Users with open UA/UC observations assigned to them and pending
              closure responsibility.
            </p>
          </div>

          <label className="uauc-users-insight__sort">
            <span>Sort users</span>
            <select
              value={sortMode}
              onChange={(event) => setSortMode(event.target.value)}
            >
              <option value="risk-desc">Risk: severe first</option>
              <option value="risk-asc">Risk: low first</option>
              <option value="open-desc">Most open observations</option>
              <option value="latest-desc">Latest activity</option>
            </select>
          </label>
        </div>

        <div className="uauc-users-insight__stats">
          <div className="uauc-users-insight__stat">
            <strong>{totalOpen}</strong>
            <span>Total Open Observations</span>
          </div>

          <div className="uauc-users-insight__stat">
            <strong>{assignedUserCount}</strong>
            <span>Assigned Users</span>
          </div>

          <div className="uauc-users-insight__stat danger">
            <strong>{highestPending}</strong>
            <span>Highest Pending by One User</span>
          </div>

          <div className="uauc-users-insight__stat warning">
            <strong>{unassignedOpen}</strong>
            <span>Unassigned Open Observations</span>
          </div>
        </div>

        <div
          className="uauc-risk-summary"
          aria-label="Open observations by risk level"
        >
          <strong>Risk distribution</strong>

          <div className="uauc-risk-summary__items">
            {[1, 2, 3, 4].map((level) => (
              <div
                className={`uauc-risk-summary__item uauc-risk-summary__item--${RISK_LEVELS[level].tone}`}
                key={level}
              >
                <RiskBadge level={level} />
                <strong>{riskTotals[level]}</strong>
              </div>
            ))}

            {riskTotals.unrated > 0 ? (
              <div className="uauc-risk-summary__item uauc-risk-summary__item--unrated">
                <RiskBadge level={null} />
                <strong>{riskTotals.unrated}</strong>
              </div>
            ) : null}
          </div>
        </div>

        <div className="uauc-users-insight__grid">
          <div className="uauc-users-insight__panel">
            <div className="uauc-users-insight__panel-head">
              <h4>Major Pending Users</h4>
              <span>Top {majorUsers.length}</span>
            </div>

            <div className="uauc-major-user-list">
              {majorUsers.map((group) => (
                <button
                  type="button"
                  className="uauc-major-user-card"
                  key={group.key}
                  onClick={() => setSelectedUserGroup(group)}
                >
                  <div>
                    <strong>{getUserName(group.user)}</strong>
                    <span>{getUserDesignation(group.user)}</span>
                    <RiskBadge level={group.highestRisk} />
                  </div>

                  <em>{group.openCount}</em>
                </button>
              ))}
            </div>
          </div>

          <div className="uauc-users-insight__panel">
            <div className="uauc-users-insight__panel-head">
              <h4>All Users With Open Observations</h4>
              <span>{userGroups.length} users</span>
            </div>

            <div className="uauc-user-table-wrap">
              <table className="uauc-user-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Designation</th>
                    <th>Phone</th>
                    <th>Risk Level</th>
                    <th>Open</th>
                    <th>Latest</th>
                    <th>Details</th>
                  </tr>
                </thead>

                <tbody>
                  {sortedUserGroups.map((group) => (
                    <tr key={group.key}>
                      <td>
                        <div className="uauc-user-cell">
                          <strong>{getUserName(group.user)}</strong>
                          <span>{getUserEmail(group.user)}</span>
                        </div>
                      </td>
                      <td>{getUserDesignation(group.user)}</td>
                      <td>{getUserPhone(group.user)}</td>
                      <td>
                        <RiskBadge level={group.highestRisk} />
                      </td>
                      <td>
                        <span className="uauc-open-count">
                          {group.openCount}
                        </span>
                      </td>
                      <td>{formatDateTime(group.latestDate)}</td>
                      <td>
                        <button
                          type="button"
                          className="uauc-view-btn"
                          onClick={() => setSelectedUserGroup(group)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {selectedUserGroup ? (
        <UserDetailsPopup
          userGroup={selectedUserGroup}
          onClose={() => setSelectedUserGroup(null)}
          onOpenObservation={(row) => {
            setSelectedUserGroup(null);
            onOpenObservation?.(row);
          }}
        />
      ) : null}
    </>
  );
}
